// Top-level app component.
//
// Boots the WASM sim via useSimWorld, renders the hex map + cities,
// mounts the doctrine selection panel, surfaces tile + city info on click,
// and forwards interaction state into the test bridge for E2E.
//
// App() only wires state: the hook groups live in ./shell/use*.ts and the
// match layout's sections in ./shell/Match*.tsx (ROADMAP 7.14). The hook call
// order below is the order the hooks had when this was one 1000-line function.

import type { MatchSetup } from '@aod/protocol';
import { PlayerHud } from '@aod/ui';
import { useCallback, useState } from 'react';
import { useChampionMarkers } from './champion/useChampionMarkers.js';
import { CinematicOverlay } from './cinematics/CinematicOverlay.js';
import { CoachmarkHost } from './coachmark/CoachmarkHost.js';
import { useCoachmarks } from './coachmark/useCoachmarks.js';
import { useMatchCoachmarkSequence } from './coachmark/useMatchCoachmarkSequence.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { FooterHint } from './components/FooterHint.js';
import { HelpModal } from './components/HelpModal.js';
import { MobileHudToggles } from './components/MobileHudToggles.js';
import { Splash } from './components/Splash.js';
import { TopBar } from './components/TopBar.js';
import { readRenderStats } from './render/renderStats.js';
import { SettingsModal } from './settings/SettingsModal.js';
import { useSettings } from './settings/useSettings.js';
import { applyAssistsAtMatchStart, clampSpeed, hintPolicy } from './setup/clientAssists.js';
import { saveLastSetup } from './setup/lastSetup.js';
import {
  type SetupDraft,
  draftFromPreset,
  humanAssistsOf,
  toMatchSetup,
} from './setup/setupModel.js';
import { MatchBoard } from './shell/MatchBoard.js';
import { MatchCombatOverlays } from './shell/MatchCombatOverlays.js';
import { MatchMinimapDock } from './shell/MatchMinimapDock.js';
import { MatchRightRail } from './shell/MatchRightRail.js';
import { MatchToastLayer } from './shell/MatchToastLayer.js';
import { MatchVictoryLayer } from './shell/MatchVictoryLayer.js';
import { ModeScreen, hasModeScreen } from './shell/ModeScreen.js';
import { useBoardActions } from './shell/useBoardActions.js';
import { useCombatView } from './shell/useCombatView.js';
import { useContinueSave } from './shell/useContinueSave.js';
import { useGameAudio } from './shell/useGameAudio.js';
import { useGameFlow } from './shell/useGameFlow.js';
import { useGraduation } from './shell/useGraduation.js';
import { useMinimapWiring } from './shell/useMinimapWiring.js';
import {
  type PauseOverride,
  useMatchCinematics,
  usePauseWiring,
} from './shell/usePauseAndCinematics.js';
import { useSelectionFlight, useSinglePlayerBridge } from './shell/useSelectionAndBridge.js';
import { useShellUiState } from './shell/useShellUiState.js';
import { useSimFeeds } from './shell/useSimFeeds.js';
import { useSimWorld } from './sim/useSimWorld/index.js';
import { useApplyTheme } from './theme/useApplyTheme.js';
import { DoctrinePanel } from './ui/DoctrinePanel/index.js';
import { EventLog } from './ui/EventLog.js';
import { DockedDebugHud } from './ui/layout/DockedDebugHud.js';
import { usePanelDockStyle } from './ui/layout/PanelDockControls.js';
import { usePanelLayoutSession } from './ui/layout/usePanelLayoutSession.js';
import { useMinimapExtras } from './ui/minimapSupport.js';
import { useDeployGarrison } from './useDeployGarrison.js';
import { useDerivedMatchState } from './useDerivedMatchState.js';
import { useGameActions } from './useGameActions.js';
import { useKeyboardShortcuts } from './useKeyboardShortcuts.js';

const LOCAL_PLAYER_INDEX = 0;

export function App() {
  useApplyTheme();
  // One session fetch feeds the layout store's per-player key (one-shot
  // stored-layout apply; guest until the session resolves).
  usePanelLayoutSession();
  const flow = useGameFlow();
  const { gameMode, setGameMode, setLocalPlayerName } = flow;

  // The setup chosen on the New game screen. Each Start makes a new object,
  // which reboots the sim with its ruleset + seats (useSimWorld deps).
  const [matchSetup, setMatchSetup] = useState<MatchSetup | null>(null);
  /** The preset's pause-on-danger for the current match (see handleStartMatch). */
  const [pauseOverride, setPauseOverride] = useState<PauseOverride | null>(null);
  const [lastSetup, setLastSetup] = useState<SetupDraft | null>(null);
  const sim = useSimWorld({
    matchSeed: 42,
    localPlayerIndex: LOCAL_PLAYER_INDEX,
    ruleset: matchSetup?.ruleset,
    seats: matchSetup?.seats,
    mapJson: matchSetup?.map,
  });
  const humanAssists = humanAssistsOf(matchSetup);
  const speedCap = humanAssists?.speedCap ?? 4;
  const simSetSpeed = sim.setSpeed;
  const setSpeedCapped = useCallback(
    (multiplier: number) => simSetSpeed(clampSpeed(multiplier, speedCap)),
    [simSetSpeed, speedCap],
  );
  const ui = useShellUiState();
  const { selection, setSelection, flyCameraTo, mobilePanel } = ui;

  // Single coachmark controller instance shared with CoachmarkHost, the
  // HelpButton mounted inside TopBar, and the sequence driver. Per the
  // integration requirement, this is the only useCoachmarks() call in the
  // tree — child components take the controller via props.
  const coachmarkController = useCoachmarks();
  // `startScreenVisible` drives the menu-side `start-game` coachmark. While
  // the menu is mounted, the sequence adapter treats the `match.started`
  // trigger as active so the start-game hint can open against the real
  // MenuScreen Start Game button. Once the player clicks Start, gameMode
  // flips away from 'menu' and the flag becomes false.
  const startScreenVisible = gameMode === 'menu';
  useMatchCoachmarkSequence({
    sim,
    controller: coachmarkController,
    startScreenVisible,
    autoOpen: hintPolicy(humanAssists?.hints).autoOpen,
  });

  // Start a single-player match from the setup screen (Start or Quick start):
  // remember the setup, apply the assists the client handles itself, and
  // hand the ruleset + seats to the sim.
  const resetCoachmarks = coachmarkController.resetAll;
  const startFromSetup = useCallback(
    (draft: SetupDraft) => {
      saveLastSetup(draft);
      const setup = toMatchSetup(draft);
      applyAssistsAtMatchStart(draft.humanAssists, {
        // For this match only: the player's own Settings choice is kept,
        // and wins again as soon as they change it mid-match.
        setPauseOnDanger: (value) =>
          setPauseOverride({ value, settingAtStart: useSettings.getState().pauseOnDanger }),
        resetCoachmarks,
      });
      setMatchSetup(setup);
      setGameMode('single');
    },
    [resetCoachmarks, setGameMode],
  );

  const combatOverlayMode = useSettings((s) => s.combatOverlayMode);

  const feeds = useSimFeeds(sim, LOCAL_PLAYER_INDEX);
  const { continueSave, setPendingLoad } = useContinueSave(sim, gameMode);

  const pauseController = usePauseWiring(sim, gameMode, pauseOverride, LOCAL_PLAYER_INDEX);
  const { cinematics, finalePlaying } = useMatchCinematics(
    sim,
    gameMode,
    pauseController,
    humanAssists,
    LOCAL_PLAYER_INDEX,
  );

  useGraduation(sim, matchSetup, cinematics, LOCAL_PLAYER_INDEX);
  const combatView = useCombatView(sim, LOCAL_PLAYER_INDEX);
  useGameAudio(sim, gameMode, ui.cameraTarget, LOCAL_PLAYER_INDEX);

  // ` (backtick) toggles debug HUD; Esc clears selection.
  useKeyboardShortcuts({
    onToggleDebug: () => ui.setShowDebug((v) => !v),
    onClearSelection: () => {
      setSelection(null);
      ui.setCameraRequest(null);
    },
  });

  const handleDeployGarrison = useDeployGarrison(sim, LOCAL_PLAYER_INDEX);
  const { suggestions, handleApplySuggestion, handleMoveClick } = useBoardActions(
    sim,
    selection,
    humanAssists?.autoManageCities === 'suggest',
  );
  const minimap = useMinimapWiring(sim, LOCAL_PLAYER_INDEX, flyCameraTo);

  const {
    handleSetPlayerName,
    handleTileClick,
    handleCityClick,
    handleUnitClick,
    handleBackgroundClick,
    closeSelection,
    startNewMatch,
    handleSave,
    handleLoad,
    handleStartBuilding,
    handleSetCityDoctrine,
  } = useGameActions({ sim, selection, setSelection, setLocalPlayerName });

  // Derived match state — selected node, unit list, building catalog, player
  // color map, and garrison count at the selected city.
  const { selected, unitNodes, buildings, playerColors, garrisonCount } = useDerivedMatchState({
    snapshot: sim.snapshot,
    selection,
    playerHudEntries: sim.playerHudEntries,
  });

  // Champion star on the minimap (empty unless legends.enabled is on).
  const championMarkers = useChampionMarkers(sim.snapshot, LOCAL_PLAYER_INDEX, playerColors);
  const minimapExtras = useMinimapExtras(sim.snapshot, LOCAL_PLAYER_INDEX);
  // The minimap's dock size (width) rides on this wrapper's inline style.
  const minimapDockStyle = usePanelDockStyle('minimap', true);

  useSelectionFlight(selected, selection, flyCameraTo);
  useSinglePlayerBridge(
    sim,
    gameMode,
    LOCAL_PLAYER_INDEX,
    selection,
    setSelection,
    feeds.attackToasts,
  );

  if (hasModeScreen(flow)) {
    return (
      <ModeScreen
        flow={flow}
        coachmarkController={coachmarkController}
        continueSave={continueSave}
        onLoadSave={setPendingLoad}
        lastSetup={lastSetup}
        setLastSetup={setLastSetup}
        onStartFromSetup={startFromSetup}
        onSetPlayerName={handleSetPlayerName}
      />
    );
  }

  if (sim.status === 'loading') return <Splash text="Loading sim core…" />;
  if (sim.status === 'error') return <Splash text={`Failed to load sim: ${sim.error}`} error />;
  if (sim.snapshot === null) return <Splash text="Loading sim core…" />;

  return (
    <ErrorBoundary>
      <div className="relative h-full w-full">
        <TopBar
          food={sim.localTreasury.food}
          industry={sim.localTreasury.industry}
          knowledge={sim.localTreasury.knowledge}
          faith={sim.localTreasury.faith}
          treasury={sim.localTreasury.gold}
          tick={sim.tick}
          runState={sim.runState}
          speed={sim.speed}
          researchState={sim.researchState}
          faithCrisis={sim.faithCrisis}
          startResearch={sim.startResearch}
          pilgrimage={sim.pilgrimage}
          reform={sim.reform}
          pause={sim.pause}
          resume={sim.resume}
          setSpeed={setSpeedCapped}
          maxSpeed={speedCap}
          onNewMatch={startNewMatch}
          onSave={handleSave}
          onLoad={handleLoad}
          onReturnToMenu={() => setGameMode('menu')}
          onOpenSettings={() => ui.setSettingsOpen(true)}
          coachmarkController={coachmarkController}
          coachmarkOpen={ui.coachmarkMenuOpen}
          onCoachmarkOpenChange={ui.setCoachmarkMenuOpen}
          onOpenReferenceHelp={() => ui.setHelpOpen(true)}
        />
        <MatchBoard
          sim={sim}
          snapshot={sim.snapshot}
          selection={selection}
          cameraRequest={ui.cameraRequest}
          engagements={combatOverlayMode === 'off' ? undefined : combatView.engagements}
          currentTick={combatView.simTick}
          onTileHover={ui.setHoveredTile}
          onUnitHover={ui.setHoveredUnit}
          onTileClick={handleTileClick}
          onCityClick={handleCityClick}
          onUnitClick={handleUnitClick}
          onMoveClick={handleMoveClick}
          onBackgroundClick={() => {
            ui.setCameraRequest(null);
            handleBackgroundClick();
          }}
        />
        <DoctrinePanel
          state={sim.localPlayer}
          currentTick={sim.tick}
          onInvest={sim.invest}
          mobileOpen={mobilePanel === 'doctrine'}
        />
        {/*
          RightPanel is the persistent right-rail panel. It always mounts so the
          Overview + Diplomacy tabs are reachable without first clicking a tile;
          the Selection tab auto-appears inside the same shell when `selected`
          becomes truthy. RightPanel itself handles the null/selected branching.
        */}
        <MatchRightRail
          sim={sim}
          snapshot={sim.snapshot}
          localPlayerIndex={LOCAL_PLAYER_INDEX}
          feeds={feeds}
          selected={selected}
          garrisonCount={garrisonCount}
          buildings={buildings}
          playerColors={playerColors}
          suggestions={suggestions}
          mobileOpen={mobilePanel === 'info'}
          onDeploy={handleDeployGarrison}
          onStartBuilding={handleStartBuilding}
          onSetDoctrine={handleSetCityDoctrine}
          onApplySuggestion={handleApplySuggestion}
          onClose={closeSelection}
        />
        <MatchVictoryLayer
          sim={sim}
          localPlayerIndex={LOCAL_PLAYER_INDEX}
          selected={selected}
          selection={selection}
          unitNodes={unitNodes}
          showPostGameSummary={ui.showPostGameSummary}
          finalePlaying={finalePlaying}
          playedPreset={matchSetup?.ruleset.preset ?? null}
          onSelectUnit={(id) => setSelection({ kind: 'unit', id })}
          onNewMatch={startNewMatch}
          onReturnToMenu={() => setGameMode('menu')}
          onViewSummary={() => ui.setShowPostGameSummary(true)}
          onCloseSummary={() => ui.setShowPostGameSummary(false)}
          onTryPreset={(id) => {
            setLastSetup(draftFromPreset(id));
            setGameMode('setup');
          }}
        />
        <MatchToastLayer
          sim={sim}
          localPlayerIndex={LOCAL_PLAYER_INDEX}
          feeds={feeds}
          pauseCard={pauseController.card}
          onResumeFromCard={pauseController.resumeFromCard}
          onDismissCard={pauseController.dismissCard}
          onGoTo={flyCameraTo}
          hoveredTile={ui.hoveredTile}
          hoveredUnit={ui.hoveredUnit}
        />
        <PlayerHud
          players={sim.playerHudEntries}
          className="absolute top-(--hud-strip-top) left-1/2 z-30 w-max max-w-[min(1100px,calc(100vw-1rem))] -translate-x-1/2 shadow-sm"
        />
        <EventLog
          events={minimap.logEvents}
          onEventClick={minimap.handleEventClick}
          className={mobilePanel === 'none' ? '' : 'max-md:hidden'}
        />
        <MatchMinimapDock
          sim={sim}
          mobilePanel={mobilePanel}
          dockStyle={minimapDockStyle}
          markers={championMarkers}
          extras={minimapExtras}
          wiring={minimap}
        />
        <DockedDebugHud
          visible={ui.showDebug}
          tick={sim.tick}
          entityCount={sim.tileCount + sim.cityCount}
          determinismHash={sim.determinismHash}
          wsState={gameMode === 'multiplayer-match' ? sim.wsState : undefined}
          getRenderStats={readRenderStats}
          // Top-left of the centre column, clear of both rails. Desktop
          // only — there is no free column on narrow screens.
          positionClassName="fixed top-[var(--hud-panel-top)] right-auto left-[var(--centre-inset)] z-30 hidden opacity-90 lg:block"
        />
        <MobileHudToggles active={mobilePanel} onChange={ui.setMobilePanel} />
        <FooterHint />
        <SettingsModal open={ui.settingsOpen} onClose={() => ui.setSettingsOpen(false)} />
        <HelpModal open={ui.helpOpen} onClose={() => ui.setHelpOpen(false)} />
        <MatchCombatOverlays
          mode={combatOverlayMode}
          view={combatView}
          modalOpen={ui.combatModalOpen}
          setModalOpen={ui.setCombatModalOpen}
          selectedIndex={ui.combatSelectedIndex}
          setSelectedIndex={ui.setCombatSelectedIndex}
        />
        {/* Hints wait while a pause card is up, so they never stack on it. */}
        {!pauseController.card && <CoachmarkHost controller={coachmarkController} />}
        <CinematicOverlay director={cinematics.director} localPlayer={LOCAL_PLAYER_INDEX} />
      </div>
    </ErrorBoundary>
  );
}
